import useToast from 'hooks/useToast';
import useUser from 'hooks/useUser';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
  FC,
  useMemo,
} from 'react';
import { Logger } from 'utils/logger';
import {
  HEARTBEAT_INTERVAL,
  HEARTBEAT_TIMEOUT,
  MAX_WS_RECONNECT_ATTEMPTS,
} from 'constant/websocket';

interface WebSocketProviderProps {
  url: string;
  token?: string;
  onMessage?: (event: MessageEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (event: Event) => void;
  children: ReactNode;
}

interface WebSocketContextProps {
  isConnected: boolean;
  socketStatus: string;
  receivedMessage: string;
  sendMessage: (message: string) => void;
  showDownloadHandler: boolean;
  setShowDownloadHandler: (show: boolean) => void;
  downloadData: unknown[];
  setDownloadData: (data: unknown[]) => void;
  setReceivedMessage: (message: string) => void;
  scanLoading: boolean;
  setScanLoading: (loading: boolean) => void;
  setIsScanComplete: (complete: boolean) => void;
  isScanComplete: boolean;
  openScanInProgressModal: boolean;
  setOpenScanInProgressModal: (open: boolean) => void;
  disconnectWebSocket?: () => void;
}

const WebSocketContext = createContext<WebSocketContextProps | undefined>(
  undefined,
);

const getReconnectDelay = (attempt: number) =>
  Math.min(1000 * 2 ** attempt + Math.random() * 1000, 30_000);

export const WebSocketProvider: FC<WebSocketProviderProps> = ({
  url,
  token,
  onMessage,
  onOpen,
  onClose,
  onError,
  children,
}) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [socketStatus, setSocketStatus] = useState<string>('');
  const [receivedMessage, setReceivedMessage] = useState<string>('');
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalDisconnectRef = useRef(false);

  // PDF DOWNLOAD
  const [showDownloadHandler, setShowDownloadHandler] = useState(false);
  const [downloadData, setDownloadData] = useState<unknown[]>([]);
  const [isScanComplete, setIsScanComplete] = useState(false);

  // BILLS SCAN
  const [scanLoading, setScanLoading] = useState(false);
  const [openScanInProgressModal, setOpenScanInProgressModal] = useState(false);

  // ACCOUNT INTEGRATION SYNC
  const [billMessages, setBillMessages] = useState<unknown[]>([]);
  const [poMessages, setPoMessages] = useState<unknown[]>([]);
  const [trackingCategorySyncMessages, setTrackingCategorySyncMessages] =
    useState<unknown[]>([]);
  const [employeeSyncMessages, setEmployeeSyncMessages] = useState<unknown[]>(
    [],
  );
  const [payItemSyncMessages, setPayItemSyncMessages] = useState<unknown[]>([]);
  const [myobEmployeeSyncMessages, setMyobEmployeeSyncMessages] = useState<
    unknown[]
  >([]);
  const [myobPayItemSyncMessages, setMyobPayItemSyncMessages] = useState<
    unknown[]
  >([]);

  const toast = useToast();
  const { user } = useUser();

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current)
      clearInterval(heartbeatIntervalRef.current);
    if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current?.readyState !== WebSocket.OPEN) return;

      socketRef.current.send(JSON.stringify({ type: 'ping' }));

      // Force-close if pong doesn't arrive → triggers onclose → reconnect
      heartbeatTimeoutRef.current = setTimeout(() => {
        Logger.log({
          message: '💔 Heartbeat timed out — forcing reconnect [DOWNLOAD]',
          enableInProduction: true,
        });
        socketRef.current?.close();
      }, HEARTBEAT_TIMEOUT);
    }, HEARTBEAT_INTERVAL);
  }, [stopHeartbeat]);

  // creates connection
  const connectWebSocket = useCallback(() => {
    const authToken = token || localStorage.getItem('token') || '';
    if (!authToken) return;

    const urlWithToken = `${url}?token=${encodeURIComponent(authToken)}`;

    Logger.log({
      message: '🚀 Attempting WebSocket connection... [DOWNLOAD]',
      enableInProduction: true,
    });

    socketRef.current = new WebSocket(urlWithToken);

    socketRef.current.onopen = () => {
      Logger.log({
        message: '✅ WebSocket connection established successfully! [DOWNLOAD]',
        enableInProduction: true,
      });
      reconnectAttemptsRef.current = 0;
      intentionalDisconnectRef.current = false;
      setIsConnected(true);
      setSocketStatus('Connected');
      startHeartbeat();
      if (onOpen) onOpen();
    };

    socketRef.current.onmessage = (event: MessageEvent) => {
      const message = event.data;

      try {
        const parsedMessage = JSON.parse(message);

        // Absorb pong — reset the heartbeat death timer
        if (parsedMessage?.msg_type === 'pong') {
          Logger.log({
            message: '✅ Pong received [DOWNLOAD]',
            enableInProduction: true,
          });
          if (heartbeatTimeoutRef.current)
            clearTimeout(heartbeatTimeoutRef.current);
          return;
        }

        // Handle rate limit errors
        if (parsedMessage?.data?.rate_limit_error) {
          toast.toast.dismiss();
          toast.error(
            parsedMessage.data?.rate_limit_error || 'Rate limit reached',
          );
          setReceivedMessage(message);
          return;
        }

        // Handle AI Budget Parser messages
      } catch {
        Logger.error({
          message: '❌ Error parsing WS message: [DOWNLOAD]',
          data: message,
          enableInProduction: true,
        });
      }

      setReceivedMessage(message);
      onMessage?.(event);
    };

    socketRef.current.onclose = () => {
      Logger.log({
        message: '❌ WebSocket connection closed [DOWNLOAD]',
        enableInProduction: true,
      });
      setIsConnected(false);
      setSocketStatus('Disconnected');
      stopHeartbeat();
      if (onClose) onClose();

      if (intentionalDisconnectRef.current) return;

      const freshToken = token || localStorage.getItem('token') || '';
      if (!freshToken) return;

      if (reconnectAttemptsRef.current >= MAX_WS_RECONNECT_ATTEMPTS) {
        Logger.log({
          message: '🛑 Max reconnect attempts reached [DOWNLOAD]',
          enableInProduction: true,
        });
        setSocketStatus('Failed');
        return;
      }

      const delay = getReconnectDelay(reconnectAttemptsRef.current);
      reconnectAttemptsRef.current += 1;
      Logger.log({
        message: `🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}) [DOWNLOAD]`,
        enableInProduction: true,
      });

      reconnectTimerRef.current = setTimeout(() => connectWebSocket(), delay);
    };

    socketRef.current.onerror = (event: Event) => {
      Logger.log({
        message: '⚠️ WebSocket connection error: [DOWNLOAD]',
        data: event,
        enableInProduction: true,
      });

      if (onError) onError(event);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    url,
    token,
    onMessage,
    onOpen,
    onClose,
    onError,
    startHeartbeat,
    stopHeartbeat,
  ]);

  useEffect(() => {
    if (!socketRef.current) {
      connectWebSocket();
    }

    return () => {
      stopHeartbeat();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (socketRef.current) {
        socketRef.current.onclose = null; // prevent reconnect on cleanup
        socketRef.current.close();
        socketRef.current = null; // allow re-mount to open a fresh connection
        Logger.log({
          message: '🔄 WebSocket connection closed on unmount. [DOWNLOAD]',
          enableInProduction: true,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const sendMessage = useCallback((message: string) => {
    if (socketRef?.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(message);
    } else {
      Logger.log({
        message: '🚫 WebSocket is not open. Message not sent. [DOWNLOAD]',
        enableInProduction: true,
      });
    }
  }, []);

  const disconnectWebSocket = useCallback(() => {
    intentionalDisconnectRef.current = true;
    stopHeartbeat();
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (socketRef.current) {
      socketRef.current.onclose = null; // prevent auto-reconnect
      socketRef.current.close();
      socketRef.current = null;
      Logger.log({
        message: '🔄 WebSocket intentionally disconnected. [DOWNLOAD]',
        enableInProduction: true,
      });
    }
    setIsConnected(false);
    setSocketStatus('Disconnected');
  }, [stopHeartbeat]);

  const contextValue = useMemo(
    () => ({
      isConnected,
      socketStatus,
      receivedMessage,
      sendMessage,
      showDownloadHandler,
      setShowDownloadHandler,
      downloadData,
      setDownloadData,
      setReceivedMessage,
      scanLoading,
      setScanLoading,
      setIsScanComplete,
      isScanComplete,
      openScanInProgressModal,
      setOpenScanInProgressModal,
      billMessages,
      setBillMessages,
      poMessages,
      setPoMessages,
      trackingCategorySyncMessages,
      setTrackingCategorySyncMessages,
      employeeSyncMessages,
      setEmployeeSyncMessages,
      payItemSyncMessages,
      setPayItemSyncMessages,
      myobEmployeeSyncMessages,
      setMyobEmployeeSyncMessages,
      myobPayItemSyncMessages,
      setMyobPayItemSyncMessages,
      disconnectWebSocket,
    }),
    [
      isConnected,
      socketStatus,
      receivedMessage,
      sendMessage,
      showDownloadHandler,
      setShowDownloadHandler,
      downloadData,
      setDownloadData,
      setReceivedMessage,
      scanLoading,
      setScanLoading,
      setIsScanComplete,
      isScanComplete,
      openScanInProgressModal,
      setOpenScanInProgressModal,
      billMessages,
      setBillMessages,
      poMessages,
      setPoMessages,
      trackingCategorySyncMessages,
      setTrackingCategorySyncMessages,
      employeeSyncMessages,
      setEmployeeSyncMessages,
      payItemSyncMessages,
      setPayItemSyncMessages,
      myobEmployeeSyncMessages,
      setMyobEmployeeSyncMessages,
      myobPayItemSyncMessages,
      setMyobPayItemSyncMessages,
      disconnectWebSocket,
    ],
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
